import { clx } from "@hm/fundamental"
import { useCallback, useEffect, useRef } from "react"

interface MonthsGridProps {
	monthNames: string[]
	isMonthSelected: (monthIndex: number) => boolean
	isMonthInRange: (monthIndex: number) => boolean
	onMonthClick: (monthIndex: number) => void
	onMonthHover: (monthIndex: number) => void
	onMouseLeave: () => void
	"data-cy": string
}

export const MonthsGrid: React.FC<MonthsGridProps> = ({
	monthNames,
	isMonthSelected,
	isMonthInRange,
	onMonthClick,
	onMonthHover,
	onMouseLeave,
	"data-cy": dataCy,
}) => {
	const gridRef = useRef<HTMLTableElement>(null)

	const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
		const allButtons = Array.from(
			gridRef.current?.querySelectorAll<HTMLButtonElement>("button") ??
				[],
		)
		const currentIndex = allButtons.indexOf(e.target as HTMLButtonElement)
		if (currentIndex === -1) return

		let nextIndex: number | null = null

		switch (e.key) {
			case "ArrowRight":
				nextIndex = currentIndex + 1
				break
			case "ArrowLeft":
				nextIndex = currentIndex - 1
				break
			case "ArrowDown":
				nextIndex = currentIndex + 3
				break
			case "ArrowUp":
				nextIndex = currentIndex - 3
				break
			default:
				return
		}

		if (
			nextIndex !== null &&
			nextIndex >= 0 &&
			nextIndex < allButtons.length
		) {
			e.preventDefault()
			allButtons[nextIndex].focus()
		}
	}, [])

	useEffect(() => {
		const el = gridRef.current
		if (!el) return
		const handler = () => onMouseLeave()
		el.addEventListener("mouseleave", handler)
		return () => el.removeEventListener("mouseleave", handler)
	}, [onMouseLeave])

	const tabbableIndex = monthNames.findIndex((_, idx) => isMonthSelected(idx))
	const tabbableMonth = tabbableIndex === -1 ? 0 : tabbableIndex

	const rows: string[][] = []
	for (let i = 0; i < monthNames.length; i += 3) {
		rows.push(monthNames.slice(i, i + 3))
	}

	return (
		<table
			ref={gridRef}
			aria-label="Months"
			className="w-full border-collapse"
			data-cy={`${dataCy}-months-grid`}
		>
			<tbody>
				{rows.map((row, rowIdx) => (
					<tr key={row[0]}>
						{row.map((month, colIdx) => {
							const idx = rowIdx * 3 + colIdx
							const selected = isMonthSelected(idx)
							const inRange = isMonthInRange(idx)

							return (
								<td
									key={month}
									aria-selected={selected}
									className="p-1"
								>
									<button
										type="button"
										onKeyDown={handleKeyDown}
										tabIndex={
											idx === tabbableMonth ? 0 : -1
										}
										onClick={() => onMonthClick(idx)}
										onMouseEnter={() => onMonthHover(idx)}
										className={clx(
											"w-full py-2 px-1 text-sm rounded-sm transition-colors cursor-pointer",
											"hover:bg-primary_faded_20",
											inRange &&
												!selected &&
												"bg-primary_faded_20",
											selected &&
												"!bg-primary !text-white",
										)}
										data-cy={`${dataCy}-month-${idx}`}
									>
										{month}
									</button>
								</td>
							)
						})}
					</tr>
				))}
			</tbody>
		</table>
	)
}
